import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { mockData } from '../mock';
import { API } from '../lib/api';

const TrainingBooking = () => {
  const [bookingData, setBookingData] = useState({
    program: '',
    preferredDate: '',
    name: '',
    email: '',
    phone: '',
    location: 'bangalore',
    experience: 'beginner',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBookingData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      const apiData = {
        program: bookingData.program,
        preferred_date: bookingData.preferredDate,
        name: bookingData.name,
        email: bookingData.email,
        phone: bookingData.phone,
        location: bookingData.location,
        experience: bookingData.experience,
        message: bookingData.message
      };
      
      const response = await fetch(`${API}/training/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData)
      });
      
      if (response.ok) {
        const result = await response.json();
        setIsSubmitting(false);
        setSubmitStatus('success');
        setBookingData({
          program: '',
          preferredDate: '',
          name: '',
          email: '',
          phone: '',
          location: 'bangalore',
          experience: 'beginner',
          message: ''
        });
        
        // Reset status after 8 seconds
        setTimeout(() => setSubmitStatus(null), 8000);
      } else {
        const errorData = await response.json();
        console.error('Training booking error response:', errorData);
        
        // Handle Pydantic validation errors
        if (errorData.detail && Array.isArray(errorData.detail)) {
          const validationErrors = errorData.detail.map(error => {
            const field = error.loc ? error.loc[error.loc.length - 1] : 'field';
            return `${field}: ${error.msg || 'Invalid value'}`;
          });
          throw new Error(`Validation errors:\n${validationErrors.join('\n')}`);
        }
        
        throw new Error(errorData.detail || errorData.message || 'Failed to submit booking');
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      setIsSubmitting(false);
      setErrorMessage(error.message || 'Failed to submit booking. Please try again.');
      setSubmitStatus('error');
      
      // Reset error status after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
        setErrorMessage('');
      }, 5000);
    }
  };

  const experienceLevels = [
    { value: 'beginner', label: 'Beginner - No prior experience' },
    { value: 'intermediate', label: 'Intermediate - Some detailing knowledge' },
    { value: 'advanced', label: 'Advanced - Looking to enhance skills' },
    { value: 'business', label: 'Business Owner - Want to start detailing business' }
  ];

  // Get minimum date (tomorrow)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <section className="py-20 bg-white" id="booking">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-4xl font-bold text-gray-900">
            Book Your
            <span className="text-teal-600"> Training Session</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to master automotive detailing? Reserve your spot in our professional training programs and start your journey to excellence.
          </p>
        </div>

        {/* Success Message */}
        {submitStatus === 'success' && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 mb-8 text-center">
            <CheckCircle className="w-12 h-12 text-teal-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-teal-800 mb-2">Booking Request Submitted!</h3>
            <p className="text-teal-700">
              Thank you for your interest in our training programs. We'll contact you within 24 hours to confirm your booking and provide detailed program information.
            </p>
          </div>
        )}

        {/* Error Message */}
        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-red-800 mb-2">Booking Submission Failed</h3>
            <p className="text-red-700">
              {errorMessage || 'There was an error submitting your booking. Please try again or contact us directly.'}
            </p>
          </div>
        )}

        {/* Booking Form */}
        <div className="bg-slate-50 rounded-2xl p-8 border border-gray-200">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Training Program Selection */}
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">Select Training Program</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {mockData.training.programs.map((program) => (
                  <label
                    key={program.id}
                    className={`cursor-pointer p-4 rounded-lg border-2 transition-all duration-200 ${
                      bookingData.program === program.name
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 bg-white hover:border-teal-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="program"
                      value={program.name}
                      checked={bookingData.program === program.name}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="font-bold text-gray-900 text-sm">{program.name}</h4>
                        <span className="text-teal-600 font-semibold text-xs">{program.duration}</span>
                      </div>
                      <p className="text-gray-600 text-xs leading-relaxed">{program.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Personal Information */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900">Personal Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900">
                    Full Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 text-gray-400" size={20} />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={bookingData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:border-teal-500 focus:outline-none transition-colors"
                      placeholder="Enter your full name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 text-gray-400" size={20} />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={bookingData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:border-teal-500 focus:outline-none transition-colors"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 text-gray-400" size={20} />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={bookingData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:border-teal-500 focus:outline-none transition-colors"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="preferredDate" className="block text-sm font-semibold text-gray-900">
                    Preferred Start Date *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 text-gray-400" size={20} />
                    <input
                      type="date"
                      id="preferredDate"
                      name="preferredDate"
                      value={bookingData.preferredDate}
                      onChange={handleInputChange}
                      min={minDate}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-teal-500 focus:outline-none transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Training Preferences */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900">Training Preferences</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="location" className="block text-sm font-semibold text-gray-900">
                    Training Location *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 text-gray-400" size={20} />
                    <select
                      id="location"
                      name="location"
                      value={bookingData.location}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-teal-500 focus:outline-none transition-colors"
                    >
                      <option value="bangalore">Bangalore, India</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="experience" className="block text-sm font-semibold text-gray-900">
                    Experience Level
                  </label>
                  <select
                    id="experience"
                    name="experience"
                    value={bookingData.experience}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-teal-500 focus:outline-none transition-colors"
                  >
                    {experienceLevels.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Additional Message */}
            <div className="space-y-2">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-900">
                Additional Notes (Optional)
              </label>
              <textarea
                id="message"
                name="message"
                value={bookingData.message}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:border-teal-500 focus:outline-none transition-colors resize-none"
                placeholder="Any specific questions or requirements for your training..."
              />
            </div>

            {/* Training Information Box */}
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
              <h4 className="font-bold text-teal-800 mb-3">What to Expect</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-teal-700">
                <div className="flex items-center space-x-2">
                  <CheckCircle size={16} />
                  <span>Hands-on practical training</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle size={16} />
                  <span>Industry-certified instructors</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle size={16} />
                  <span>Professional equipment provided</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle size={16} />
                  <span>Business guidance included</span>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !bookingData.program}
              className="w-full bg-teal-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:transform-none flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Submitting Booking...</span>
                </>
              ) : (
                <>
                  <Send size={20} />
                  <span>Book Training Session</span>
                </>
              )}
            </button>

            {/* Contact Information */}
            <div className="text-center pt-6 border-t border-gray-200">
              <p className="text-gray-600 text-sm mb-2">
                Need help choosing the right program? Contact us directly:
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center text-sm">
                <a href={`tel:${mockData.contact.bangalore.phone}`} className="text-teal-600 hover:text-teal-700 font-semibold">
                  📞 {mockData.contact.bangalore.phone}
                </a>
                <a href={`mailto:${mockData.contact.bangalore.email}`} className="text-teal-600 hover:text-teal-700 font-semibold">
                  ✉️ {mockData.contact.bangalore.email}
                </a>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default TrainingBooking;